﻿import {translation} from "@2d-front-monorepo/core";
import {MvvmButton} from "@2d-front-monorepo/design-library";
import {
	CreateOrSelectVehicleComponent,
	CreateOrSelectVehicleModel, DefaultParkingCategory,
	getVehicleConfiguration, toFormAttachments,
	VehicleId, Vehicles,
	VehicleType
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";

import {JSX, useCallback, useMemo} from "react";
import {API_ENDPOINT} from "../../../api-endpoint";
import {PermitsBackButton} from "../../../components/PermitsBackButton";
import {getGlobalContext} from "../../../hooks/getGlobalContext";
import {getHttpClient} from "../../../hooks/getHttpClient";
import {ReloadEntities, reloadPermits} from "../../../hooks/reloadPermits";
import {CreateVehicleProps} from "../../Registrations";

interface CreateOrEditVehicleModel extends CreateOrSelectVehicleModel {
}

export default function Create(props: CreateVehicleProps): JSX.Element {
	const {permits, holder, permit, vehicle, behavior, configuration, createParts, children} = props;
	const {installation} = getGlobalContext();

	const addressData = installation.configuration.addressData;
	const vehicleData = installation.configuration.vehicleData;

	const oldVehicleId = vehicle?.id ?? "";
	const vehicleType = behavior?.type ?? vehicle?.type ?? VehicleType.Resident;
	const realConfiguration = configuration ?? getVehicleConfiguration(installation, vehicleType, permit?.category);

	const availableVehicles = useMemo(() => {
		return behavior == null
			? permits.vehicles.filter(v => v.type === vehicleType && v.id !== oldVehicleId)
			: Vehicles.getAll(holder, behavior.ids);
	}, [permits.vehicles, vehicleType, oldVehicleId, holder]);

	const plates = useMemo(() => availableVehicles.map(x => x.plate), [availableVehicles]);

	const initialState = useMemo<CreateOrEditVehicleModel>(() => {
		return {
			vehicleId: "create" as VehicleId,
			arguments: {
				plate: "",
				type: vehicleType,
				data: {
					nickname: "",
					color: "",
					model: "",
					type: "",
					province: installation.configuration.vehicles.defaultProvince ?? ""
				},
				attachments: [],
				customData: []
			},
			customData: {},
			attachments: {}
		}
	}, [vehicle?.id, vehicleType]);

	const onSubmit = useCallback(async function (result: CreateOrSelectVehicleModel): Promise<void> {
		const httpClient = getHttpClient();
		const category = permit?.category ?? DefaultParkingCategory;
		const attachments = await toFormAttachments(httpClient, result.attachments);

		let newVehicleId: VehicleId;
		let reload: ReloadEntities = ReloadEntities.All;

		if (result.vehicleId === "create") {
			reload = ReloadEntities.Holder;
			newVehicleId = await httpClient.vehicles.create({
				attachments: attachments,
				customData: result.arguments.customData,
				data: result.arguments.data,
				plate: result.arguments.plate,
				type: result.arguments.type,
				category: category
			});

		} else {
			newVehicleId = result.vehicleId;
			if (attachments.length > 0) {
				await httpClient.vehicles.updateAttachments(newVehicleId, {
					attachments: attachments,
					category: category
				});
			}
		}

		if (permit != null && vehicle != null) {
			reload |= ReloadEntities.Permits;
			await httpClient.permits.replaceVehicle(permit.id, {
				oldVehicleId: vehicle.id,
				newVehicleId: newVehicleId
				// status: ApprovalStatus.Submitted
			});
		}

		await reloadPermits(reload);
		window.history.back();

	}, [vehicle, permit]);

	const header = permit == null ? PermitTranslations.vehicles_page__title__new : PermitTranslations.vehicles_page__title__edit;
	return <>
		<h1>{translation(header)}</h1>
		<hr/>
		<div className="layout vertical gap">
			{children}
			<CreateOrSelectVehicleComponent
				colors={vehicleData.colors}
				models={vehicleData.models}
				defaultProvince={installation.configuration.vehicles.defaultProvince}
				provinces={addressData.provinces}
				filesPrefix={API_ENDPOINT.fileDownload}
				initialState={initialState}
				vehicles={availableVehicles}
				createEnabled={behavior?.addNew ?? true}
				onSubmit={onSubmit}
				plateValidation={{existing: plates}}
				createParts={createParts}
				configuration={realConfiguration}
				footer={() => <div className="form__buttons form__buttons--bottom">
					<MvvmButton kind="primary">{translation(PermitTranslations.save_changes)}</MvvmButton>
					<span></span>
					<PermitsBackButton/>
				</div>}/>
		</div>
	</>;
}
